// @ts-nocheck
import paper from 'paper';
import * as Flatmodels from '@medsurf/flat-models';
import { Marker } from './marker/marker';
import { Keymap } from './marker/keymap';
import { KeymapSelftest } from './marker/keymapSelftest';
import { Freeform } from './freeform/freeform';

/**
 * Canvas Container
 */
export class Container {
  /**
   * Members
   */
  private _elements: any[];
  private _selection: any[];
  private _dirty: boolean;
  private _minZoom: any;
  private _offset: { x: number; y: number };
  private width: number;
  private height: number;
  private _isMicroscopeContainer = true;
  private _groups: Flatmodels.GroupEntityModels.Group[];
  public isDrawing = false;

  /**
   * Constructor
   *
   * @param _localPaper
   * @param _format
   */
  public constructor(private _localPaper,
                     private _format) {
    this._elements = [];
    this._selection = [];
    this._dirty = true;
    this._minZoom = null;
    this._offset = {x: 0, y: 0};
  }

  /**
   * Getter scale
   */
  public get scale() {
    if (this._isMicroscopeContainer) {
      return this.getElementById('image').scale;
    }
  }

  /**
   * Getter offset
   */
  public get offset() {
    return this._offset;
  }

  /**
   * Getter elements
   */
  public get elements(): any[] {
    return this._elements;
  }

  /**
   * Getter selection
   */
  public get selection() {
    return this._selection;
  }

  /**
   * Getter markers
   */
  public get markers() {
    return this._elements.filter(element => {
      return element instanceof Marker || element instanceof Freeform || element instanceof Keymap;
    });
  }

  /**
   * Getter minZoom
   */
  public get minZoom(): number {
    return this._minZoom;
  }

  /**
   * Setter groups
   *
   * @param groups
   */
  set groups(groups: Flatmodels.GroupEntityModels.Group[]) {
    this._groups = groups;
  }

  /**
   * Round
   *
   * @param value
   * @returns {number}
   */
  public static round(value: number): number {
    return Math.round(value);
  }

  /**
   * Add element to container
   *
   * @param element
   */
  public addElement(element) {
    this._elements.push(element);
    this.setHierarchy();
    this._dirty = true;
  }

  /**
   * Remove element
   *
   * @param element
   */
  public removeElement(element) {
    for (let i = this._elements.length; i--;) {
      if (this._elements[i] === element) {
        if (this._elements[i] instanceof paper.PointText) {
          this._elements[i].remove();
          this._elements.splice(i, 1);
          this._dirty = true;
          break;
        } else {
          if (this._localPaper) {
            this._elements[i].cleanUp();
          }
          this._elements.splice(i, 1);
          this._dirty = true;
          break;
        }
      }
    }
  }

  /**
   * Resize container
   */
  public resize() {
    const width = this.getViewportSize().width,
      height = this.getViewportSize().height;
    for (const element of this._elements) {
      element.resize(width, height);
      element.arrange();
    }
  }

  /**
   * Call element arrange function
   */
  public arrange() {
    for (const element of this._elements) {
      element.arrange();
      if (element instanceof Marker && element.element) {
        element.element.bringToFront();
      }
    }
    for (const element of this._elements) {
      if (element instanceof Marker && element.useSymbol && element.element) {
        element.element.bringToFront();
      }
    }
    this._dirty = true;
  }

  /**
   * Update canvas
   */
  public update() {
    if (this._dirty) {
      this._localPaper.view.draw();
      this._dirty = false;
    }
  }

  /**
   * Show markers
   *
   * @param markers
   */
  public showMarkers(markers) {
    for (const element of this._elements) {
      if (element instanceof Marker || element instanceof Freeform || element instanceof Keymap) {
        element.state = markers ? 'visible' : 'hidden';
      }
    }
    this._dirty = true;
  }

  /**
   * Set canvas size
   *
   * @param width
   * @param height
   */
  public setCanvasSize(width: number,
                       height: number) {
    this.width = width;
    this.height = height;
  }

  /**
   * Set min zoom
   *
   * @param value
   */
  public setMinZoom(value: number) {
    this._minZoom = value;
  }

  /**
   * Set Hierarchy
   */
  public setHierarchy() {
    const markers = this._elements.filter(element => element instanceof Marker);
    const freeforms = this._elements.filter(element => element instanceof Freeform);
    const rest = this._elements.filter(element => !(element instanceof Freeform || element instanceof Marker));
    this._elements = [...rest, ...freeforms, ...markers];
  }

  /**
   * Select element
   *
   * @param item
   * @param add
   */
  public select(item,
                add) {
    if (!item) {
      return;
    }
    if (!add) {
      this.deselectAll();
    }
    this._selection.push(item);
    item.select();
    this._dirty = true;
    this.arrange();
    this.update();
  }

  /**
   * Select All
   */
  public selectAll() {
    for (const element of this._elements) {
      if (element instanceof Marker || element instanceof Keymap || element instanceof Freeform) {
        this._selection.push(element);
        element.select();
      }
    }
    this._dirty = true;
    this.arrange();
    this.update();
  }

  /**
   * unselect all elements
   */
  public deselectAll() {
    for (const element of this._elements) {
      if (element instanceof Marker || element instanceof Keymap || element instanceof Freeform) {
        element.unselect();
      }
    }
    this._selection = [];
    this._dirty = true;
    this.arrange();
    this.update();
  }

  /**
   * Calculate Fix Dimensions
   *
   * @param size
   */
  public calculateFixDimensions(size) {
    const zoom = this._localPaper.view.zoom * 100;
    return Math.floor(size * (100 / zoom));
  }

  /**
   * Get viewport size
   */
  public getViewportSize() {
    return this._localPaper.view.viewSize;
  }

  /**
   * Get canvas size
   */
  public getCanvasSize() {
    return {
      width: this.width,
      height: this.height
    };
  }

  /**
   * Get canvas dimensions
   */
  public getCanvasDimensions() {
    if (!this._isMicroscopeContainer) {
      const element = this.getElementById('image');
      return {
        width: this._format.canvas.width * element.scale,
        height: this._format.canvas.height * element.scale
      };
    } else {
      return {
        width: this._format.canvas.width,
        height: this._format.canvas.height
      };
    }
  }

  /**
   * Get element by id
   *
   * @param id
   */
  public getElementById(id: string) {
    return this._elements.filter(element => element.id === id)[0];
  }

  /**
   * Get group by id
   *
   * @param id
   */
  public getGroupById(id: string): Flatmodels.GroupEntityModels.Group {
    return this._groups?.filter((element: Flatmodels.GroupEntityModels.Group) => element.id === id).shift();
  }

  /**
   * Get Selftest Marker Element By Id
   *
   * @param id
   */
  public getSelftestMarkerElementById(id) {
    return this._elements
        .filter(function (element) {
          return element instanceof KeymapSelftest;
        })
        .filter(element => element.model.id === id)[0];
  }

  /**
   * Get relative coords
   *
   * @param x
   * @param y
   * @param offset
   * @param scale
   */
  public getRelativeCoords(x: number,
                           y: number,
                           offset: { x: number, y: number },
                           scale: number) {
    if (this._isMicroscopeContainer) {
      return {
        x: x / this.getCanvasSize().width,
        y: y / this.getCanvasSize().height
      };
    } else {
      return {
        x: (x / this.getCanvasDimensions().width) - this.offset.x,
        y: (y / this.getCanvasDimensions().height) - this.offset.y
      };
    }
  }

  /**
   * Get relative delta
   *
   * @param x
   * @param y
   */
  public getRelativeDelta(x: number,
                          y: number) {
    return this._isMicroscopeContainer ?
      {x, y} :
      {
        x: 1 / (this.getCanvasDimensions().width * this.scale) * x,
        y: 1 / (this.getCanvasDimensions().height * this.scale) * y
      };
  }

  /**
   * Get absolute delta
   *
   * @param x
   * @param y
   */
  public getAbsoluteDelta(x: number,
                   y: number) {
    return this._isMicroscopeContainer ?
      {x: x * this.getCanvasSize().width, y: y * this.getCanvasSize().height} :
      {
        x: 1 / (this.getCanvasDimensions().width * this.scale) * x,
        y: 1 / (this.getCanvasDimensions().height * this.scale) * y
      };
  }

  /**
   * Get absolute coords
   *
   * @param x
   * @param y
   * @param offset
   * @param scale
   */
  public getAbsoluteCoords(x: number,
                           y: number,
                           offset: { x: number, y: number },
                           scale: number) {
    if (this._isMicroscopeContainer) {
      return {
        x: Container.round(x * this.getCanvasSize().width),
        y: Container.round(y * this.getCanvasSize().height)
      };
    } else {
      return {
        x: Container.round((x * this.getCanvasDimensions().width) + this.offset.x),
        y: Container.round((y * this.getCanvasDimensions().height) + this.offset.y)
      };
    }
  }

  /**
   * Get Stroke Width
   *
   * @param zoom
   */
  public getStrokeWidth(zoom: number) {
    if (this._isMicroscopeContainer) {
      return Math.round((this.getCanvasSize().width / zoom) * (0.02 / 12));
    }
    return null;
  }

  /**
   * Get absolute coords marker tool
   *
   * @param x
   * @param y
   */
  public getAbsoluteCoordsMarkerTool(x: number,
                                     y: number) {
    return this._isMicroscopeContainer ? {x: x * this.getCanvasSize().width, y: y * this.getCanvasSize().height} : {
      x: Container.round((x * this.getCanvasDimensions().width) + this.offset.x),
      y: Container.round((y * this.getCanvasDimensions().height) + this.offset.y)
    };
  }

  /**
   * Destroy elements
   */
  public destroyElements() {
    for (let i = this._elements.length; i--;) {
      let element = this._elements[i];
      if (element instanceof paper.PointText) {
        element.remove();
        this._elements.splice(i, 1);
        element = null;
      }
      if (this._localPaper && element) {
        element.cleanUp();
      }
      this._elements.splice(i, 1);
      element = null;
    }
  }

  /**
   * Clean up
   */
  public cleanUp() {
    for (let i = this._elements.length; i--;) {
      let element = this._elements[i];
      if (element instanceof paper.PointText) {
        element.remove();
      } else {
        if (this._localPaper) {
          element.cleanUp();
        }
      }
      element = null;
    }
    this._elements = [];
  }
}
